import { useEffect, useState } from 'react';
import { sessionStorageUtils } from '@/lib/storageUtils';

const STORAGE_KEY = 'volt_anaf_wait_started_at';
// The ANAF answer usually comes within this time.
const FILL_MS = 75_000;
// Never full before the answer: the bar waits here.
const MAX_PROGRESS = 0.9;
const TICK_MS = 250;
// Forgets a wait that ended without this screen, so a later one starts over.
const START_TTL_MS = 30 * 60 * 1000;

// Kept for the tab session, so a reload goes on from the real progress.
const getStartedAt = () => {
  const now = Date.now();
  const stored = sessionStorageUtils.get<number>(STORAGE_KEY);
  if (typeof stored === 'number' && stored <= now) {
    return stored;
  }
  sessionStorageUtils.set(STORAGE_KEY, now, START_TTL_MS);
  return now;
};

export const getAnafWaitProgress = (elapsedMs: number) =>
  Math.min(Math.max(elapsedMs, 0) / FILL_MS, 1) * MAX_PROGRESS;

/**
 * Time-based progress of the ANAF income check: fills to 90% in 75 seconds
 * and waits there. `finish` fills it and forgets the start for the next wait.
 */
export const useAnafWaitProgress = () => {
  const [startedAt] = useState(getStartedAt);
  const [now, setNow] = useState(Date.now);
  const [isFinished, setIsFinished] = useState(false);
  const elapsedMs = now - startedAt;
  const isFilled = elapsedMs >= FILL_MS;

  useEffect(() => {
    if (isFilled) {
      return;
    }
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [isFilled]);

  const finish = () => {
    sessionStorageUtils.remove(STORAGE_KEY);
    setIsFinished(true);
  };

  return {
    progress: isFinished ? 1 : getAnafWaitProgress(elapsedMs),
    finish,
  };
};
